import { useCallback, useEffect } from 'react';

import { Key, KeyCode } from '../enums/Keyboard';

type UseKeyboardNavigation = (
  options: UseKeyboardNavigationOptions
) => (event: KeyboardEvent) => void;

interface UseKeyboardNavigationOptions {
  onGoNext: () => void;
  onGoPrevious: () => void;
  onSelect: () => void;
}

export const useKeyboardClose = (isOpen: boolean, closeHandler: () => void) => {
  useEffect(() => {
    if (isOpen) {
      const keyDownHandler = ({ key }: KeyboardEvent) => (key === Key.Esc ? closeHandler() : null);

      document.addEventListener('keydown', keyDownHandler);

      return () => document.removeEventListener('keydown', keyDownHandler);
    }
  }, [isOpen, closeHandler]);
};

export const useKeyboardSelect = (
  onSelect: (arg?: unknown, event?: KeyboardEvent) => void,
  callbackArg?: unknown
) =>
  useCallback(
    event => {
      if ([KeyCode.Enter, KeyCode.Space].includes(event.keyCode)) {
        event.preventDefault();
        event.stopPropagation();
        onSelect(callbackArg, event);
      }
    },
    [onSelect, callbackArg]
  );

export const useKeyboardNavigation: UseKeyboardNavigation = ({
  onGoNext,
  onGoPrevious,
  onSelect,
}) =>
  useCallback(
    (event: KeyboardEvent) => {
      let actionPerformed = false;
      switch (event.keyCode) {
        case KeyCode.ArrowUp:
        case KeyCode.ArrowLeft:
          onGoPrevious();
          actionPerformed = true;
          break;
        case KeyCode.ArrowDown:
        case KeyCode.ArrowRight:
          onGoNext();
          actionPerformed = true;
          break;
        case KeyCode.Space:
          onSelect();
          actionPerformed = true;
          break;
      }
      if (actionPerformed) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [onGoNext, onGoPrevious, onSelect]
  );
